import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  visuallyHidden,
  zIndexLayers,
} from '../styles';
import { Container, Button } from './ui';
import Logo from './Logo';
import Navigation from './Navigation';
import Search from './Search';

const StyledHeader = styled.header`
  padding: 15px 0;
  background-color: ${brandColours.quaternary};

  ${minBreakpointQuery.small`
    padding-top: 20px;
    padding-bottom: 20px;
  `}

  ${minBreakpointQuery.large`
     position: relative;
  `}

  ${({ isDetail }) => {
    if (isDetail) {
      return css`
        ${minBreakpointQuery.large`
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          z-index: 1;
          background: none;
        `}
      `;
    }
  }}
`;

const StyledInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
`;

const StyledSearchWrapper = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;

  ${maxBreakpointQuery.large`
    margin-left: auto;
  `}

  ${minBreakpointQuery.large`
    order: 4;
  `}

  ${({ display }) => {
    if (display) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
`;

const StyledButtons = styled.div`
  ${maxBreakpointQuery.large`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.large`
    white-space: nowrap;
    z-index: ${zIndexLayers.fourth};
  `}
`;

const StyledLoginButton = styled(Button)`
  ${minBreakpointQuery.large`
    margin-right: 10px;
    padding: 14px 32px;
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.xlarge`
    margin-right: 15px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-right: 20px;
  `}
`;

const StyledDemoButton = styled(Button)`
  ${minBreakpointQuery.large`
    padding: 13px 16px;
    ${fontSize(14)};
    background: none;
    border: 1px solid ${standardColours.white};
  `}
`;

const Header = ({ isDetail }) => {
  const {
    datoCmsHeader: { logoLink, loginLink, demoLink, enableChristmasLogo },
  } = useStaticQuery(graphql`
    query HeaderQuery {
      datoCmsHeader {
        logoLink {
          pageUrl {
            ...LinkFragment
          }
        }
        loginLink {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        demoLink {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        enableChristmasLogo
      }
    }
  `);

  const [displayNav, setDisplayNav] = useState(false);
  const [displaySearch, setDisplaySearch] = useState(false);

  return (
    <StyledHeader isDetail={isDetail}>
      <Container wide={true}>
        <StyledInner>
          <Logo
            link={logoLink}
            isHeader={true}
            enableChristmasLogo={enableChristmasLogo}
          />
          <StyledSearchWrapper display={displayNav}>
            <Search
              displaySearch={displaySearch}
              setDisplaySearch={setDisplaySearch}
            />
            <StyledButtons>
              <StyledLoginButton to={loginLink.pageUrl}>
                {loginLink.text}
              </StyledLoginButton>
              <StyledDemoButton to={demoLink.pageUrl}>
                {demoLink.text}
              </StyledDemoButton>
            </StyledButtons>
          </StyledSearchWrapper>
          <Navigation
            displayNav={displayNav}
            setDisplayNav={setDisplayNav}
            displaySearch={displaySearch}
          />
        </StyledInner>
      </Container>
    </StyledHeader>
  );
};

export default Header;
